import React, { useRef, useEffect, useImperativeHandle, forwardRef, useState } from "react";
import DOMPurify from "dompurify";
import "./RichInputBox.css";

import { useConstants } from "../../constant/constantProvider";
import useIsMobile from "../../hooks/useIsMobile";

// assets
import closeIcon from "../../assets/new-images/close-icon.svg";

const RichInputBox = forwardRef(({
    value = "",
    onChange,
    isTextBoxDisabled = false,
    isLoading = false,
    placeholder = "Ask me anything...",
    handleKeyPress,
    setIsTextareaFocused,
    charLimit,
    isEnhancing = false,
}, ref) => {
    const isMobile = useIsMobile();
    const { ENHANCING_PROMPT } = useConstants();
    const editorRef = useRef(null);
    const [isLimitExceeded, setIsLimitExceeded] = useState(false);

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
        clear: () => {
            if (editorRef.current) {
                editorRef.current.innerHTML = "";
                if (onChange) {
                    onChange("");
                }
            }
        },
        focus: () => {
            if (editorRef.current) {
                editorRef.current.focus();
            }
        }
    }));

    // Sync value from parent when component is not focused
    useEffect(() => {
        if (editorRef.current && document.activeElement !== editorRef.current) {
            const currentHTML = editorRef.current.innerHTML || "";
            if (currentHTML !== value) {
                editorRef.current.innerHTML = DOMPurify.sanitize(value || "", {
                    ALLOWED_TAGS: ["strong", "b", "table", "thead", "tbody", "tr", "td", "th", "br"],
                    ALLOWED_ATTR: ["border", "style"],
                });
            }
            if (charLimit && editorRef.current) {
                const text = editorRef.current.textContent || "";
                setIsLimitExceeded(text.length > charLimit);
            }
        }
    }, [value, charLimit]);

    // Simple input handler
    const handleInput = (e) => {
        const html = e.currentTarget.innerHTML || "";

        // Clean up empty content for placeholder
        if (html.trim() === "" || html === "<br>") {
            e.currentTarget.innerHTML = "";
        }

        if (onChange) {
            // Normalize &nbsp; to regular spaces and trim trailing/leading whitespace
            let cleaned = html
                .replace(/&nbsp;/gi, " ")
                .replace(/\u00A0/g, " ")
                .trim();

            // Trim leading and trailing <br> tags
            cleaned = cleaned.replace(/^(<br\s*\/?>\s*)+/gi, '')
                .replace(/(<br\s*\/?>\s*)+$/gi, '')
                .trim();

            onChange(cleaned); // send cleaned HTML to parent
        }

        if (charLimit) {
            const text = e.currentTarget.textContent || "";
            setIsLimitExceeded(text.length >= charLimit);
        }
    };

    // Simple key handler
    const handleKeyDown = (e) => {
        if (isLoading) {
            e.preventDefault();
            return;
        }

        if (e.key === "Enter" && !isMobile && !e.shiftKey) {
            e.preventDefault();
            if (handleKeyPress) {
                handleKeyPress(e);
            }
            return;
        }

        if (charLimit) {
            const text = editorRef.current.textContent || "";
            const isAllowedKey = [
                "Backspace", "Delete", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown",
                "Home", "End", "PageUp", "PageDown", "Tab",
            ].includes(e.key);

            if (!isAllowedKey && !e.metaKey && !e.ctrlKey && !e.altKey && text.length >= charLimit) {
                // Check if user has selected text (replacing selected text might reduce length)
                const selection = window.getSelection();
                const selectedText = selection.toString();

                if (selectedText.length === 0) {
                    e.preventDefault();
                    setIsLimitExceeded(true);
                }
            }
        }
    };

    // Handle key up to clean up empty content for placeholder
    const handleKeyUp = (e) => {
        if (e.key === "Backspace" || e.key === "Delete") {
            const text = e.currentTarget.textContent || "";
            if (text.trim() === "") {
                e.currentTarget.innerHTML = "";
                if (onChange) {
                    onChange("");
                }
            }
        }
    };

    // Simple focus handlers
    const handleFocus = () => {
        if (isMobile && setIsTextareaFocused) {
            setIsTextareaFocused(true);
        }
    };

    const handleBlur = () => {
        if (isMobile && setIsTextareaFocused) {
            setIsTextareaFocused(false);
        }
    };

    // Normalize pasted content - tables and bold stay as HTML, everything else becomes plain text
    const handlePaste = (e) => {
        e.preventDefault();

        const clipboardData = e.clipboardData || window.clipboardData;
        const htmlData = clipboardData.getData("text/html");
        const textData = clipboardData.getData("text/plain");

        let cleanContent = "";
        let hasHtmlContent = false;

        if (htmlData) {
            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = DOMPurify.sanitize(htmlData);

            // Remove unwanted elements
            const unwantedElements = tempDiv.querySelectorAll("img, picture, figure, svg, canvas, video, audio, iframe, embed, object, script, style");
            unwantedElements.forEach((el) => el.remove());

            // Process tables - clean and add borders
            const tables = tempDiv.querySelectorAll("table");
            tables.forEach((table) => {
                table.removeAttribute("class");
                table.removeAttribute("bgcolor");
                table.removeAttribute("background");
                table.setAttribute("border", "1");
                table.setAttribute("style", "border-collapse: collapse;");

                table.querySelectorAll("*").forEach((el) => {
                    el.removeAttribute("class");
                    el.removeAttribute("bgcolor");
                    el.removeAttribute("background");

                    if (el.tagName === "TD" || el.tagName === "TH") {
                        el.setAttribute("style", "border: 1px solid black; padding: 4px 8px;");
                    } else {
                        el.removeAttribute("style");
                    }
                });
            });

            // Convert font-weight styles to <strong> tags
            const elementsWithBold = tempDiv.querySelectorAll("[style*='font-weight']");
            elementsWithBold.forEach((el) => {
                const style = el.getAttribute("style") || "";
                const fontWeight = style.match(/font-weight\s*:\s*(\d+|bold|bolder)/i);
                if (fontWeight) {
                    const weight = fontWeight[1].toLowerCase();
                    if (weight === "bold" || weight === "bolder" || parseInt(weight) >= 600) {
                        // Wrap content in strong tag
                        const strong = document.createElement("strong");
                        strong.innerHTML = DOMPurify.sanitize(el.innerHTML);
                        el.innerHTML = "";
                        el.appendChild(strong);
                    }
                }
            });

            // Process all elements - remove styles except for allowed elements
            const allElements = tempDiv.querySelectorAll("*");
            allElements.forEach((el) => {
                const tagName = el.tagName.toUpperCase();

                // Keep these tags as-is (they provide formatting)
                const allowedTags = ["STRONG", "B", "TABLE", "THEAD", "TBODY", "TR", "TD", "TH"];

                if (!allowedTags.includes(tagName)) {
                    // Remove all attributes except href for links
                    const attributes = Array.from(el.attributes);
                    attributes.forEach((attr) => {
                        if (attr.name !== "href") {
                            el.removeAttribute(attr.name);
                        }
                    });
                }
            });

            // Check if we have meaningful HTML (tables or bold)
            const hasTables = tempDiv.querySelectorAll("table").length > 0;
            const hasBold = tempDiv.querySelectorAll("strong, b").length > 0;

            if (hasTables || hasBold) {
                hasHtmlContent = true;
                cleanContent = tempDiv.innerHTML;
            } else {
                // No meaningful HTML, use plain text
                cleanContent = textData;
            }
        } else {
            cleanContent = textData;
        }

        // Insert content
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);

            // Calculate available space
            if (charLimit) {
                const currentText = editorRef.current.textContent || "";
                const selectedText = selection.toString();
                const availableSpace = charLimit - (currentText.length - selectedText.length);

                if (availableSpace <= 0) {
                    setIsLimitExceeded(true);
                    return;
                }

                // Truncate content if needed. Note: Truncating HTML is complex, doing simple text truncation for now if mixed.
                // If we have HTML content, we might lose tags if we naive substring.
                // Ideally we should warn or just paste what fits.
                // For simplicity/safety, if HTML + truncation needed -> convert to text or try best effort.
                // Here we will truncate the cleanContent (which is HTML string or text) based on text length approximation.

                const tempDivForLength = document.createElement("div");
                tempDivForLength.innerHTML = DOMPurify.sanitize(cleanContent);
                const pasteTextLength = tempDivForLength.textContent.length;

                if (pasteTextLength > availableSpace) {
                    // If it doesn't fit, we might need to truncate
                    // Truncating HTML string safely is hard without a library. 
                    // Fallback to text truncation if meaningful HTML is present but doesn't fit?
                    // Or just block? User asked "not able to write and paste".
                    // Let's try to truncate text-only version if it doesn't fit, or text part if no HTML.

                    if (hasHtmlContent) {
                        // If HTML doesn't fit, maybe just block it or insert text version truncated?
                        // Let's insert truncated text version to be safe and ensure limit.
                        const textToPaste = tempDivForLength.textContent.substring(0, availableSpace);
                        cleanContent = textToPaste;
                        hasHtmlContent = false;
                        setIsLimitExceeded(true);
                    } else {
                        cleanContent = cleanContent.substring(0, availableSpace);
                        setIsLimitExceeded(true);
                    }
                }
            }

            range.deleteContents();

            if (hasHtmlContent) {
                // Sanitize content before inserting as HTML to prevent XSS
                const sanitizedContent = DOMPurify.sanitize(cleanContent, {
                    ALLOWED_TAGS: ["strong", "b", "table", "thead", "tbody", "tr", "td", "th", "br", "div", "p", "span", "a"],
                    ALLOWED_ATTR: ["href", "border", "style"],
                });
                const fragment = document.createRange().createContextualFragment(sanitizedContent);
                range.insertNode(fragment);
            } else {
                const textNode = document.createTextNode(cleanContent);
                range.insertNode(textNode);
            }

            range.collapse(false);
            selection.removeAllRanges();
            selection.addRange(range);
        }

        // Trigger onChange
        if (onChange && editorRef.current) {
            const html = editorRef.current.innerHTML || "";
            let cleaned = html
                .replace(/&nbsp;/gi, " ")
                .replace(/\u00A0/g, " ")
                .trim();

            // Trim leading and trailing <br> tags
            cleaned = cleaned.replace(/^(<br\s*\/?>\s*)+/gi, '')
                .replace(/(<br\s*\/?>\s*)+$/gi, '')
                .trim();

            onChange(cleaned);
            if (charLimit) {
                const text = editorRef.current.textContent || "";
                setIsLimitExceeded(text.length >= charLimit);
            }
        }
    };

    const handleClear = () => {
        if (editorRef.current) {
            editorRef.current.innerHTML = "";
            setIsLimitExceeded(false);
            if (onChange) {
                onChange("");
            }
            editorRef.current.focus();
        }
    };

    const isEditable = !isTextBoxDisabled && !isLoading;

    return (
        <div className="rich-input-container">
            {isEnhancing && (
                <div className="input-enhancing-loader">
                    <span className="input-enhancing-dot"></span>
                    <span className="input-enhancing-dot"></span>
                    <span className="input-enhancing-dot"></span>
                    <span className="input-enhancing-label">{ENHANCING_PROMPT}</span>
                </div>
            )}
            <div
                ref={editorRef}
                contentEditable={isEditable}
                className={`input-box ${!isEditable ? "disabled" : ""} ${isLimitExceeded ? "limit-exceeded" : ""} ${isEnhancing ? "input-box-hidden" : ""}`}
                suppressContentEditableWarning={true}
                onInput={handleInput}
                onKeyDown={handleKeyDown}
                onKeyUp={handleKeyUp}
                onPaste={handlePaste}
                onFocus={handleFocus}
                onBlur={handleBlur}
                data-placeholder={placeholder}
            />
            {value && isEditable && (
                <button
                    type="button"
                    className="rich-input-clear-btn"
                    onClick={handleClear}
                    title="Clear text"
                >
                    <img src={closeIcon} alt="Clear" className="rich-input-clear-icon" />
                </button>
            )}
            {isLimitExceeded && (
                <div className="limit-error-message">Character Count Exceeded</div>
            )}
        </div>
    );
});

RichInputBox.displayName = "RichInputBox";

export default RichInputBox;